import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Percent, Building2, Layers } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => setS(false)); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        platform_fee_percent: parseFloat(s.platform_fee_percent),
        default_distributor_share: parseFloat(s.default_distributor_share),
        brand_name: s.brand_name,
        reserve: parseFloat(s.reserve) || 0,
      });
      setS(data);
      toast.success("Settings saved");
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Platform Settings</h2>
        <p className="text-sm text-muted-foreground">Global commission configuration applied to new accounts.</p>
      </div>

      <form onSubmit={save}>
        <Card className="p-6 space-y-5">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /> Brand name</Label>
            <Input value={s.brand_name || ""} onChange={(e) => setS({ ...s, brand_name: e.target.value })} data-testid="settings-brand-input" />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Percent className="h-4 w-4" /> Default platform fee %</Label>
              <Input type="number" step="0.1" min="0" value={s.platform_fee_percent}
                onChange={(e) => setS({ ...s, platform_fee_percent: e.target.value })} data-testid="settings-fee-input" />
              <p className="text-xs text-muted-foreground">Charged on every successful collection.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Layers className="h-4 w-4" /> Default distributor share %</Label>
              <Input type="number" step="0.1" min="0" max="100" value={s.default_distributor_share}
                onChange={(e) => setS({ ...s, default_distributor_share: e.target.value })} data-testid="settings-share-input" />
              <p className="text-xs text-muted-foreground">Distributor's cut of the platform fee.</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /> Reserve (₹)</Label>
            <Input type="number" step="1" min="0" value={s.reserve ?? 0}
              onChange={(e) => setS({ ...s, reserve: e.target.value })} data-testid="settings-reserve-input" />
            <p className="text-xs text-muted-foreground">Funds held back, shown in the top bar next to current balance.</p>
          </div>

          <div className="rounded-xl border bg-muted/40 p-4 text-sm">
            <p className="font-medium mb-2">Example on a ₹1,000 collection</p>
            <div className="space-y-1 font-mono text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Platform fee ({s.platform_fee_percent}%)</span><span className="text-warning">{(1000 * s.platform_fee_percent / 100).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">→ Distributor ({s.default_distributor_share}%)</span><span className="text-chart-2">{(1000 * s.platform_fee_percent / 100 * s.default_distributor_share / 100).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">→ Platform revenue</span><span className="text-primary">{(1000 * s.platform_fee_percent / 100 * (100 - s.default_distributor_share) / 100).toFixed(2)}</span></div>
              <div className="flex justify-between border-t pt-1 mt-1"><span>Merchant net</span><span className="text-success">{(1000 - 1000 * s.platform_fee_percent / 100).toFixed(2)}</span></div>
            </div>
          </div>

          <Button type="submit" disabled={saving} data-testid="settings-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save settings</>}
          </Button>
        </Card>
      </form>
    </div>
  );
}
